import { delay, put, select } from 'redux-saga/effects';
import i18n from 'i18n';
import * as Sentry from '@sentry/react';

import { updateActiveChipAction } from 'core/widgets/ActiveChip/actions';

import {
  saveBettingTimeToStoreAction,
  startDecrementTimeLeftAction,
} from 'core/widgets/BettingTime/actions';
import { changeRoundStateAction } from 'core/widgets/RoundStates/actions';
import { RoundStates } from 'core/widgets/RoundStates/types';
import { VideoQualities, StreamNamesType } from 'core/widgets/VideoSettings/types';

import { saveTableSettingsToStoreAction } from 'core/widgets/TableSettings/actions';
import { setBalanceAction, setTotalBetAction } from 'core/widgets/User/actions';
import { Notifications } from 'types';
import { setStreamNamesAction } from 'core/widgets/VideoSettings/action';
import {
  setCurrentJackpot,
  setNextJackpot,
  setStageJackpot,
  setUserInfo,
} from 'core/widgets/Jackpot/actions';
import { setUserInfoAuth } from 'core/widgets/Auth/actions';
import { ACTIONS, IPlayerIdStage, PositionJackpot } from 'core/widgets/Jackpot/types';
import { RestService } from 'services/RestAPIService';
import { OnPlayerConnectedData } from '../types';
import { sumBets } from 'widgets/Roulette/helpers';
import { addConfirmedBetAction, clearAllBetsAction } from 'core/widgets/RouletteBet/actions';
import { confirmedBetsSelector } from 'core/widgets/RouletteBet/selectors';
import { IClientBet } from 'widgets/Roulette/types';
import { setLastWinners } from 'core/widgets/LastWinners/actions';
import { addBettingTimeToastAction } from 'core/widgets//Toast/actions';
import { roundStatusMessages } from 'constants/toastMessages';
import { setHotColdAnalytics } from 'core/widgets//HotColdAnalytics/actions';

export function* onPlayerConnectedSaga({
  type: notificationType,
  data: result,
  ...rest
}: OnPlayerConnectedData) {
  try {
    const {
      player,
      configuration: {
        table_id: tableId,
        table_name: tableName,
        displaying_win_ball: displayingWinBall,
        displaying_win_result: displayingWinResult,
      },
      dealer: { nickname: dealerNickname },
      round,
      status: roundStatus,
      lastWinners,
      jackpots,
      jackpotOn,
      mode,
      lobbyUrl,
      closeGameMessage,
      rouletteBets,
      rouletteBetsInfo,
      hotColdAnalytic,
    } = result;

    const {
      balance,
      betsToken,
      apiToken,
      playerName,
      uuid,
      minBet: minBetLimit,
      maxBet: maxBetLimit,
      lang,
      currency,
      operatorName,
      rouletteLimits,
    } = player;

    const operatorId = operatorName.replace(/\s+/g, '-').toLowerCase();

    if (notificationType === Notifications.PLAYER_CONNECTED) {
      const { stream_high, stream_medium, stream_low } = result.configuration;
      const confirmedBets: IClientBet[] = yield select(confirmedBetsSelector);
      Sentry.setTag('uid', uuid);
      Sentry.setTag('game_id', round?.roundId);
      Sentry.setTag('operator_id', operatorName);

      yield put(setUserInfo({ playerName, uuid }));
      yield put(
        setUserInfoAuth({
          playerName,
          uuid,
          currency: currency?.toUpperCase(),
          mode,
          jackpotOn,
          lang,
          operatorId,
          lobbyUrl,
          closeGameMessage,
        }),
      );

      yield put(
        saveTableSettingsToStoreAction({
          tableId,
          tableName,
          gameId: round?.roundId || null,
          dealerNickname,
          minBetLimit,
          maxBetLimit,
          displayingWinBall: displayingWinBall ? displayingWinBall : displayingWinResult,
          rouletteLimits,
        }),
      );
      yield put(changeRoundStateAction({ roundState: roundStatus }));

      yield put(setBalanceAction(balance));

      yield put(setTotalBetAction(0));

      if (lastWinners?.length) {
        const winners = lastWinners.map((winner) => winner.toString());
        yield put(setLastWinners(winners));
      }

      if (hotColdAnalytic?.length) {
        yield put(setHotColdAnalytics(hotColdAnalytic));
      }

      if (
        roundStatus === RoundStates.BettingTimeStarted &&
        round?.finishBettingTime !== undefined
      ) {
        const date = new Date(round.finishBettingTime);
        const dateNow = new Date();
        const difference = Math.floor((date.getTime() - dateNow.getTime()) / 1000);

        yield put(
          saveBettingTimeToStoreAction({
            bettingTimeEnd: round.finishBettingTime,
            bettingTime: 0,
          }),
        );
        yield put(startDecrementTimeLeftAction());
        yield put(
          addBettingTimeToastAction({
            value: roundStatusMessages.BETTING_TIME_STARTED,
          }),
        );
      }

      const language = localStorage.getItem(`${operatorId}_${tableId}_language`)
        ? JSON.parse(localStorage.getItem(`${operatorId}_${tableId}_language`) as string)
        : null;

      if (!language) {
        i18n.changeLanguage(lang);
      }

      if (confirmedBets.length) {
        yield put(clearAllBetsAction());
      }

      if (rouletteBets?.length && roundStatus !== RoundStates.ResultConfirmed) {
        const totalBet = sumBets(rouletteBets);
        const info = rouletteBetsInfo?.length ? rouletteBetsInfo : [];

        yield put(addConfirmedBetAction([...rouletteBets, ...info]));
        yield put(setTotalBetAction(totalBet));
      }

      localStorage.setItem(`${operatorId}_${tableId}_rest_auth_token`, betsToken);
      localStorage.setItem(`${operatorId}_${tableId}_api_token`, apiToken);

      RestService.setTableID(tableId as string);
      RestService.setOperatorID(operatorId as string);

      yield put(updateActiveChipAction());

      if (stream_high && stream_medium && stream_low) {
        const streamNames: StreamNamesType = {
          [VideoQualities.High]: stream_high,
          [VideoQualities.Medium]: stream_medium,
          [VideoQualities.Low]: stream_low,
        };

        yield put(setStreamNamesAction(streamNames));
      }

      if (jackpots?.length) {
        const currentJackpot = jackpots?.find(
          (jackpot) => jackpot.position === PositionJackpot.CURRENT,
        );
        const nextJackpot = jackpots?.find((jackpot) => jackpot.position === PositionJackpot.NEXT);

        if (nextJackpot) {
          yield put(setNextJackpot(nextJackpot));
        }

        if (currentJackpot) {
          yield put(setCurrentJackpot(currentJackpot));

          if (currentJackpot.draw) {
            const playersObject = currentJackpot.draw.players?.length
              ? currentJackpot.draw.players?.reduce(
                  (acc, player) => ({
                    ...acc,
                    [player.uuid]: player,
                  }),
                  {} as { [key: string]: IPlayerIdStage },
                )
              : null;

            if (playersObject) {
              yield put(
                setStageJackpot({ playersObject, stageNumber: currentJackpot.draw.stageNumber }),
              );
            } else {
              yield put(
                setStageJackpot({
                  playersObject: null,
                  stageNumber: currentJackpot.draw.stageNumber,
                }),
              );
            }
          }

          yield delay(3000);

          yield put({ type: ACTIONS.WATCH_DATE_CONTROL_INTERVAL });
        }
      }
    }
  } catch (error) {
    Sentry.captureException(error);
  }
}
